/** Shared MyRDP admin panel classes (matches Dashboard / Layout dark theme). */

export const admin = {
  /** Full-viewport-height column layout for pages with a stretching data table */
  pageStretch: 'animate-enterUp flex min-h-[calc(100dvh-7rem)] flex-col gap-6',
  page: 'space-y-6 animate-enterUp',
  title: 'text-2xl sm:text-3xl font-semibold text-white tracking-tight',
  subtitle: 'text-sm text-[#BABABA] mt-1',
  link: 'text-sm font-medium text-[#7B9CFF] hover:text-white transition-colors',
  card:
    'rounded-2xl border border-[#B8B8B847] bg-[#FFFFFF08] backdrop-blur-sm shadow-[0_16px_44px_rgba(0,0,0,0.35)]',
  cardInner: 'p-5 sm:p-6',
  statCard:
    'group rounded-2xl border border-[#B8B8B847] bg-no-repeat bg-top-right bg-cover p-5 transition-all duration-300 hover:border-blue-300/40 hover:shadow-[0_24px_60px_rgba(60,60,255,0.15)]',
  tableWrap: 'rounded-2xl border border-[#B8B8B847] bg-[#FFFFFF06] overflow-x-auto',
  /** Table + pagination column: outer fills height, inner scrolls */
  tableShell: 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#B8B8B847] bg-[#FFFFFF06]',
  tableScroll: 'min-h-0 flex-1 overflow-x-auto overflow-y-auto',
  table: 'w-full min-w-[640px] text-sm',
  thead: 'bg-[#FFFFFF08] border-b border-[#FFFFFF10]',
  th: 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#BABABA] whitespace-nowrap',
  tbody: 'divide-y divide-[#FFFFFF0C]',
  tr: 'hover:bg-[#FFFFFF05] transition-colors',
  td: 'px-4 py-3 text-white/90 align-middle',
  tdMuted: 'text-white/55',
  iconBox: (tone) =>
    ({
      blue: 'bg-[#3C3CFF]/20 text-[#8BA3FF]',
      green: 'bg-[#009439]/20 text-[#4ADE80]',
      violet: 'bg-[#7C3AED]/20 text-[#C4B5FD]',
      amber: 'bg-[#946800]/30 text-[#FBBF24]'
    }[tone] || 'bg-white/10 text-white/80'),
  btnPrimary:
    'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-b from-[#5B73FF] to-[#3C3CFF] border border-white/10 shadow-[0_10px_28px_rgba(60,60,255,0.28)] hover:brightness-110 transition disabled:opacity-50',
  btnSecondary:
    'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white/90 bg-white/10 border border-white/15 hover:bg-white/15 transition',
  btnDanger:
    'p-2 rounded-lg text-[#FF6B6B] hover:bg-[#FF0000]/15 transition',
  btnIcon:
    'p-2 rounded-lg text-[#7B9CFF] hover:bg-[#3C3CFF]/20 transition',
  input:
    'w-full rounded-xl border border-[#EBEBEB1C] bg-[#FFFFFF0F] px-4 py-2.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-blue-400/50',
  select:
    'rounded-xl border border-[#EBEBEB1C] bg-[#1a1f35] px-4 py-2.5 text-sm text-white outline-none focus:border-blue-400/50 [&>option]:bg-[#1a1f35] [&>option]:text-white',
  modalOverlay: 'fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm',
  modalCard:
    'w-full max-w-md rounded-2xl border border-[#B8B8B847] bg-[#0f1428]/95 backdrop-blur-md p-6 shadow-[0_24px_50px_rgba(0,0,0,0.55)]',
  modalTitle: 'text-xl font-semibold text-white mb-4',
  label: 'block text-white/80 text-sm font-medium mb-2',
  quickAction:
    'flex flex-col gap-2 rounded-xl border border-[#FFFFFF14] bg-[#FFFFFF06] p-4 transition hover:border-[#3C3CFF]/45 hover:bg-[#3C3CFF]/10'
};

export function roleBadgeClass(role) {
  if (role === 'admin') return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#94000042] border-[#940000]/60 text-[#FF8A8A]';
  if (role === 'reseller')
    return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#1E3A5F]/80 border-[#3B82F6]/40 text-[#93C5FD]';
  return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-white/10 border-white/15 text-white/80';
}

export function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active' || s === 'completed')
    return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#009439]/15 border-[#009439]/45 text-[#4ADE80]';
  if (s === 'pending') return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#946800]/25 border-[#B45309]/45 text-[#FBBF24]';
  if (s === 'failed' || s === 'suspended' || s === 'expired')
    return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#7F1D1D]/40 border-[#DC2626]/45 text-[#FCA5A5]';
  return 'px-2.5 py-1 rounded-full text-xs font-semibold border bg-white/10 border-white/15 text-white/70';
}
